import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Minus, Plus, Trash2 } from 'lucide-react';
import { formatPrice } from '../data/products';
import { resolveProductImage } from '../utils/productUtils';

interface OrderProductCardProps {
  item: {
    sku: string;
    productName: string;
    color: string;
    option: string;
    quantity: number;
    price: number;
    promoPrice?: number;
    image: string;
    stockLimit?: number;
  };
  variant?: 'cart' | 'checkout' | 'checkout-cart';
  onUpdateQuantity?: (sku: string, qty: number) => void;
  onRemove?: (sku: string) => void;
  className?: string;
  badge?: React.ReactNode;
}

export const OrderProductCard: React.FC<OrderProductCardProps> = ({
  item,
  variant = 'cart',
  onUpdateQuantity,
  onRemove,
  className = '',
  badge,
}) => {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const isCart = variant === 'cart' || variant === 'checkout-cart';
  const displayPrice = item.promoPrice ?? item.price;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className={`group flex gap-4 bg-white p-3 border border-ink/5 hover:border-ink/10 transition-colors rounded-none ${className}`}
    >
      {/* Product Image */}
      <div className={`${isCart ? 'w-24 h-28' : 'w-20 h-24'} bg-paper shrink-0 overflow-hidden`}>
        <img
          src={resolveProductImage(item.image)}
          alt={item.productName || 'Produk'}
          className="w-full h-full object-cover grayscale-[0.2] group-hover:grayscale-0 transition-all duration-500"
          referrerPolicy="no-referrer"
          onError={(e) => {
            const target = e.currentTarget;
            target.onerror = null; 
            target.src = 'https://placehold.co/400x600/F4F4F4/722F38?text=SECERA';
          }}
        />
      </div>

      {/* Product Info */}
      <div className="flex-1 flex flex-col min-w-0 py-1">
        <div className="flex justify-between items-start gap-2 mb-1">
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <h3 className="text-sm font-bold text-ink tracking-tight truncate uppercase">
              {item.productName}
            </h3>
            {badge}
          </div>
          {isCart && onRemove && (
            <button
              onClick={() => setShowConfirmDelete(true)}
              className="text-muted/40 hover:text-brand-wine transition-colors p-1 -mr-1"
              aria-label="Remove item"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>

        <p className="text-[10px] text-muted/60 font-bold tracking-widest uppercase mb-2">
          {item.color} <span className="mx-1 text-ink/10">|</span> {item.option}
          {!isCart && <span className="mx-1 text-ink/10">|</span>}
          {!isCart && `x${item.quantity}`}
        </p>

        <div className="flex items-baseline gap-2">
          <span className="text-sm font-semibold text-brand-wine">
            {formatPrice(displayPrice)}
          </span>
          {item.promoPrice && (
            <span className="text-[10px] text-muted/40 line-through">
              {formatPrice(item.price)}
            </span>
          )}
        </div>

        {isCart && item.stockLimit !== undefined && item.quantity >= item.stockLimit && (
          <div className="text-[9px] text-brand-wine font-black uppercase tracking-widest mt-1 animate-pulse flex items-center gap-1">
            <span>● HANYA TERSISA {item.stockLimit} PRODUK</span>
          </div>
        )}

        {isCart ? (
          showConfirmDelete ? (
            <div className="flex items-center justify-between w-full mt-auto py-1 border-t border-ink/5 pt-2">
              <span className="text-[10px] text-brand-wine font-black uppercase tracking-wider">
                {variant === 'checkout-cart' ? 'Hapus & Batalkan Checkout?' : 'Hapus produk?'}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    onRemove?.(item.sku);
                    setShowConfirmDelete(false);
                  }}
                  className="px-3 py-1 bg-brand-wine text-white text-[9px] font-black uppercase tracking-widest hover:bg-brand-wine/90 transition-colors rounded-none"
                >
                  Ya
                </button>
                <button
                  onClick={() => setShowConfirmDelete(false)}
                  className="px-3 py-1 bg-ink/5 text-ink text-[9px] font-black uppercase tracking-widest hover:bg-ink/10 transition-colors rounded-none"
                >
                  Tidak
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between mt-auto">
              <div className="flex items-center border border-ink/10 bg-white">
                <button
                  onClick={() => {
                    if (item.quantity === 1) {
                      setShowConfirmDelete(true);
                    } else {
                      onUpdateQuantity?.(item.sku, item.quantity - 1);
                    }
                  }}
                  className="w-8 h-8 flex items-center justify-center text-ink/40 hover:text-brand-wine hover:bg-ink/[0.02] transition-colors"
                >
                  <Minus className="w-3 h-3" />
                </button>
                <span className="text-xs font-bold text-ink w-8 text-center tabular-nums">
                  {item.quantity}
                </span>
                <button
                  onClick={() => onUpdateQuantity?.(item.sku, item.quantity + 1)}
                  disabled={item.stockLimit !== undefined && item.quantity >= item.stockLimit}
                  className="w-8 h-8 flex items-center justify-center text-ink/40 hover:text-brand-wine hover:bg-ink/[0.02] disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                >
                  <Plus className="w-3 h-3" />
                </button>
              </div>
              <span className="text-[10px] text-muted/60 font-bold uppercase tracking-wider">
                Total: <span className="text-ink">{formatPrice(displayPrice * item.quantity)}</span>
              </span>
            </div>
          )
        ) : (
          <div className="mt-auto pt-2 border-t border-ink/5 flex justify-between items-center">
            <span className="text-[9px] font-bold text-muted/40 uppercase tracking-widest">Subtotal</span>
            <span className="text-sm font-bold text-ink tabular-nums">
              {formatPrice(displayPrice * item.quantity)}
            </span>
          </div>
        )}
      </div>
    </motion.div>
  );
};
